import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  FileText,
  FileUp,
  Loader2,
  PlusCircle,
  Upload,
  X as XIcon,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  applyOrderAlteration,
  previewOrderAlteration,
  type AlterationLine,
  type AlterationLineAction,
  type OrderAlterationPreview,
} from "@/lib/adminOrderAlterationApi";
import { fetchAdminProducts } from "@/lib/adminProductsApi";
import { getApiErrorMessage } from "@/lib/api";
import type { AdminProductSummary, OrderDetail } from "@/lib/types";

interface Props {
  orderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied?: (updated: OrderDetail) => void;
}

interface LineDecision {
  apply: boolean;
  // For ADD/UNRESOLVED: the chosen catalog product. Pre-filled when mapping exists.
  catalogProductId: string | null;
  // Persist the supplier-code → product mapping when applying.
  rememberMapping: boolean;
}

function formatQty(value: number | null | undefined): string {
  if (value == null) return "—";
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function OrderAlterationDialog({ orderId, open, onOpenChange, onApplied }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<OrderAlterationPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [decisions, setDecisions] = useState<Record<number, LineDecision>>({});
  const [products, setProducts] = useState<AdminProductSummary[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Reset every time the dialog (re)opens for a different order.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setDecisions({});
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [open, orderId]);

  const acceptFile = (candidate: File | null) => {
    if (!candidate) return;
    const name = candidate.name.toLowerCase();
    const isAccepted =
      candidate.type === "application/xml" ||
      candidate.type === "text/xml" ||
      name.endsWith(".xml");
    if (isAccepted) {
      setFile(candidate);
    } else {
      toast.error("Envie um arquivo .xml.");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    acceptFile(e.dataTransfer.files?.[0] ?? null);
  };

  useEffect(() => {
    if (!open || products.length > 0) return;
    let mounted = true;
    setProductsLoading(true);
    fetchAdminProducts()
      .then((list) => {
        if (mounted) setProducts(list.filter((p) => p.active));
      })
      .catch((err) => toast.error(getApiErrorMessage(err, "Não foi possível carregar produtos")))
      .finally(() => {
        if (mounted) setProductsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, products.length]);

  const productOptions = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name)),
    [products],
  );

  const runPreview = async () => {
    if (!file || !orderId) return;
    setPreviewing(true);
    try {
      const result = await previewOrderAlteration(orderId, file);
      setPreview(result);
      const initial: Record<number, LineDecision> = {};
      result.lines.forEach((line) => {
        initial[line.lineNumber] = {
          // Pre-select only actionable lines. UNRESOLVED/NO_CHANGE stay unchecked.
          apply: line.changeType === "QUANTITY_CHANGE"
            || line.changeType === "CANCEL"
            || (line.changeType === "ADD" && !!line.mappedProductId),
          catalogProductId: line.mappedProductId,
          rememberMapping: false,
        };
      });
      setDecisions(initial);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível processar o arquivo"));
    } finally {
      setPreviewing(false);
    }
  };

  const blockingErrors = useMemo(() => {
    if (!preview) return [];
    const errs: string[] = [];
    preview.lines.forEach((line) => {
      const d = decisions[line.lineNumber];
      if (!d?.apply) return;
      if (line.changeType === "ADD" || line.changeType === "UNRESOLVED") {
        if (!d.catalogProductId) {
          errs.push(`Linha ${line.lineNumber}: selecione o produto para "${line.productNameFromPdf}".`);
        }
      }
      if (line.changeType === "CANCEL" && !line.orderItemId) {
        errs.push(`Linha ${line.lineNumber}: item não encontrado no pedido para cancelar.`);
      }
    });
    return errs;
  }, [preview, decisions]);

  const counts = useMemo(() => {
    if (!preview) return { update: 0, add: 0, cancel: 0, unresolved: 0, noChange: 0, applying: 0 };
    let update = 0, add = 0, cancel = 0, unresolved = 0, noChange = 0, applyingCount = 0;
    preview.lines.forEach((line) => {
      if (line.changeType === "QUANTITY_CHANGE") update++;
      if (line.changeType === "ADD") add++;
      if (line.changeType === "CANCEL") cancel++;
      if (line.changeType === "UNRESOLVED") unresolved++;
      if (line.changeType === "NO_CHANGE") noChange++;
      if (decisions[line.lineNumber]?.apply) applyingCount++;
    });
    return { update, add, cancel, unresolved, noChange, applying: applyingCount };
  }, [preview, decisions]);

  const handleApply = async () => {
    if (!preview || !orderId) return;
    if (blockingErrors.length > 0) return;
    if (counts.applying === 0) {
      toast.error("Selecione ao menos uma alteração para aplicar.");
      return;
    }

    const actions: AlterationLineAction[] = preview.lines
      .map((line): AlterationLineAction | null => {
        const d = decisions[line.lineNumber];
        if (!d?.apply) return null;
        const type =
          line.changeType === "CANCEL"
            ? "CANCEL"
            : line.changeType === "QUANTITY_CHANGE"
            ? "QUANTITY_CHANGE"
            : "ADD";
        return {
          apply: true,
          type,
          orderItemId: line.orderItemId,
          externalCode: line.externalCode,
          catalogProductId: d.catalogProductId ?? line.mappedProductId,
          catalogUnitId: line.mappedUnitId,
          newQuantity: line.newQuantityFromPdf,
          rememberMapping: d.rememberMapping,
        } satisfies AlterationLineAction;
      })
      .filter((action): action is AlterationLineAction => action !== null);

    setApplying(true);
    try {
      const updated = await applyOrderAlteration(orderId, { actions });
      onApplied?.(updated);
      toast.success("Alterações aplicadas ao pedido.");
      onOpenChange(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível aplicar as alterações"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border bg-surface-elevated px-6 py-5">
          <DialogTitle>Alterar pedido via XML de Alteração de AF</DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {!preview && (
            <div className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/xml,text/xml,.xml"
                className="hidden"
                onChange={(e) => acceptFile(e.target.files?.[0] ?? null)}
              />
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !file && fileInputRef.current?.click()}
                className={`relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
                  isDragging
                    ? "border-primary bg-primary/10"
                    : file
                    ? "border-primary/40 bg-secondary/30"
                    : "cursor-pointer border-border hover:border-primary/60 hover:bg-secondary/40"
                }`}
              >
                {file ? (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <FileText className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium">{file.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB · XML
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-3 top-3 h-7 w-7 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      aria-label="Remover arquivo"
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                      <FileUp className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium">
                        Arraste o XML de Alteração de AF aqui
                      </div>
                      <div className="text-xs text-muted-foreground">
                        ou clique para escolher um arquivo
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      <Upload className="h-4 w-4" /> Escolher XML
                    </Button>
                  </>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="hero"
                  size="sm"
                  onClick={() => void runPreview()}
                  disabled={!file || previewing}
                >
                  {previewing && <Loader2 className="h-4 w-4 animate-spin" />}
                  Processar arquivo
                </Button>
              </div>
            </div>
          )}

          {preview && (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-900 border-blue-200">
                  {counts.update} alteração(ões) de qtde.
                </Badge>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-900 border-emerald-200">
                  {counts.add} item(ns) novo(s)
                </Badge>
                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">
                  {counts.cancel} cancelamento(s)
                </Badge>
                {counts.unresolved > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-200">
                    {counts.unresolved} sem mapeamento
                  </Badge>
                )}
                {counts.noChange > 0 && (
                  <Badge variant="outline" className="bg-secondary/40 text-muted-foreground">
                    {counts.noChange} sem mudança
                  </Badge>
                )}
              </div>

              {preview.warnings.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="mb-1 flex items-center gap-2 font-semibold">
                    <AlertCircle className="h-4 w-4" /> Atenção
                  </div>
                  <ul className="list-disc pl-5">
                    {preview.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              <AlterationGroup
                title="Alterações de quantidade"
                icon={<ArrowRight className="h-4 w-4 text-blue-700" />}
                lines={preview.lines.filter((l) => l.changeType === "QUANTITY_CHANGE")}
                decisions={decisions}
                onToggle={(line, apply) =>
                  setDecisions((d) => ({ ...d, [line.lineNumber]: { ...d[line.lineNumber], apply } }))
                }
              />

              <AlterationGroup
                title="Cancelamentos"
                icon={<XCircle className="h-4 w-4 text-destructive" />}
                lines={preview.lines.filter((l) => l.changeType === "CANCEL")}
                decisions={decisions}
                onToggle={(line, apply) =>
                  setDecisions((d) => ({ ...d, [line.lineNumber]: { ...d[line.lineNumber], apply } }))
                }
              />

              <AddOrUnresolvedGroup
                title="Itens novos"
                icon={<PlusCircle className="h-4 w-4 text-emerald-700" />}
                lines={preview.lines.filter((l) => l.changeType === "ADD")}
                products={productOptions}
                productsLoading={productsLoading}
                decisions={decisions}
                onChange={(line, patch) =>
                  setDecisions((d) => ({
                    ...d,
                    [line.lineNumber]: { ...d[line.lineNumber], ...patch },
                  }))
                }
              />

              {counts.unresolved > 0 && (
                <AddOrUnresolvedGroup
                  title="Sem mapeamento"
                  icon={<AlertCircle className="h-4 w-4 text-amber-700" />}
                  lines={preview.lines.filter((l) => l.changeType === "UNRESOLVED")}
                  products={productOptions}
                  productsLoading={productsLoading}
                  decisions={decisions}
                  onChange={(line, patch) =>
                    setDecisions((d) => ({
                      ...d,
                      [line.lineNumber]: { ...d[line.lineNumber], ...patch },
                    }))
                  }
                />
              )}

              {preview.lines.filter((l) => l.changeType === "NO_CHANGE").length > 0 && (
                <details className="rounded-lg border border-border p-3 text-sm">
                  <summary className="cursor-pointer font-medium text-muted-foreground">
                    Linhas sem alteração ({counts.noChange})
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {preview.lines
                      .filter((l) => l.changeType === "NO_CHANGE")
                      .map((l) => (
                        <li key={l.lineNumber}>
                          {l.productNameFromPdf} — {formatQty(l.newQuantityFromPdf ?? l.originalQuantityFromPdf)}{" "}
                          {l.unitFromPdf}
                        </li>
                      ))}
                  </ul>
                </details>
              )}

              {blockingErrors.length > 0 && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
                  <div className="mb-1 flex items-center gap-2 font-semibold text-destructive">
                    <AlertCircle className="h-4 w-4" /> Resolva antes de aplicar
                  </div>
                  <ul className="list-disc pl-5">
                    {blockingErrors.slice(0, 6).map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                    {blockingErrors.length > 6 && <li>… e mais {blockingErrors.length - 6}.</li>}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t border-border bg-surface-elevated px-6 py-4">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={applying}>
            Cancelar
          </Button>
          {preview && (
            <Button
              variant="hero"
              size="sm"
              onClick={() => void handleApply()}
              disabled={applying || blockingErrors.length > 0 || counts.applying === 0}
            >
              {applying && <Loader2 className="h-4 w-4 animate-spin" />}
              <CheckCircle2 className="h-4 w-4" />
              Aplicar {counts.applying} alteração(ões)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AlterationGroup({
  title,
  icon,
  lines,
  decisions,
  onToggle,
}: {
  title: string;
  icon: React.ReactNode;
  lines: AlterationLine[];
  decisions: Record<number, LineDecision>;
  onToggle: (line: AlterationLine, apply: boolean) => void;
}) {
  if (lines.length === 0) return null;
  return (
    <section className="rounded-lg border border-border bg-surface-elevated">
      <header className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-semibold">
        {icon}
        <span>{title}</span>
        <span className="text-muted-foreground">({lines.length})</span>
      </header>
      <ul className="divide-y divide-border">
        {lines.map((line) => {
          const d = decisions[line.lineNumber];
          const current = line.orderItemCurrentQuantity;
          const next = line.newQuantityFromPdf;
          return (
            <li key={line.lineNumber} className="flex items-center gap-3 px-3 py-2.5">
              <Checkbox
                checked={!!d?.apply}
                onCheckedChange={(checked) => onToggle(line, checked === true)}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">
                  {line.orderItemProductName ?? line.productNameFromPdf}
                </div>
                <div className="text-xs text-muted-foreground">
                  cód. {line.externalCode}
                  {line.unitFromPdf ? ` · ${line.unitFromPdf}` : ""}
                </div>
              </div>
              <div className="text-right text-sm tabular-nums">
                {line.changeType === "CANCEL" ? (
                  <span className="text-destructive">
                    Cancelar ({formatQty(current)} {line.unitFromPdf})
                  </span>
                ) : (
                  <span>
                    <span className="text-muted-foreground line-through">{formatQty(current)}</span>{" "}
                    <ArrowRight className="inline h-3 w-3" />{" "}
                    <span className="font-semibold">{formatQty(next)}</span>{" "}
                    <span className="text-muted-foreground">{line.unitFromPdf}</span>
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function AddOrUnresolvedGroup({
  title,
  icon,
  lines,
  products,
  productsLoading,
  decisions,
  onChange,
}: {
  title: string;
  icon: React.ReactNode;
  lines: AlterationLine[];
  products: AdminProductSummary[];
  productsLoading: boolean;
  decisions: Record<number, LineDecision>;
  onChange: (line: AlterationLine, patch: Partial<LineDecision>) => void;
}) {
  if (lines.length === 0) return null;
  return (
    <section className="rounded-lg border border-border bg-surface-elevated">
      <header className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-semibold">
        {icon}
        <span>{title}</span>
        <span className="text-muted-foreground">({lines.length})</span>
      </header>
      <ul className="divide-y divide-border">
        {lines.map((line) => {
          const d = decisions[line.lineNumber];
          return (
            <li key={line.lineNumber} className="space-y-2 px-3 py-2.5">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={!!d?.apply}
                  onCheckedChange={(checked) => onChange(line, { apply: checked === true })}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{line.productNameFromPdf}</div>
                  <div className="text-xs text-muted-foreground">
                    cód. {line.externalCode}
                    {line.unitFromPdf ? ` · ${line.unitFromPdf}` : ""}
                  </div>
                </div>
                <div className="text-right text-sm tabular-nums">
                  <span className="font-semibold">{formatQty(line.newQuantityFromPdf)}</span>{" "}
                  <span className="text-muted-foreground">{line.unitFromPdf}</span>
                </div>
              </div>
              <div className="ml-7 flex flex-wrap items-center gap-2">
                <select
                  value={d?.catalogProductId ?? ""}
                  onChange={(e) => onChange(line, { catalogProductId: e.target.value || null })}
                  className="h-9 flex-1 min-w-[240px] rounded-md border border-input bg-background px-2 text-sm"
                  disabled={productsLoading}
                >
                  <option value="">— Selecione produto —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Checkbox
                    checked={!!d?.rememberMapping}
                    onCheckedChange={(checked) => onChange(line, { rememberMapping: checked === true })}
                  />
                  Salvar mapeamento
                </label>
              </div>
              {line.suggestions.length > 0 && !d?.catalogProductId && (
                <div className="ml-7 flex flex-wrap gap-1">
                  {line.suggestions.map((s) => (
                    <button
                      key={s.productId}
                      type="button"
                      className="rounded-md border border-border px-2 py-0.5 text-xs hover:bg-secondary/60"
                      onClick={() => onChange(line, { catalogProductId: s.productId })}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
